import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
  concatMap, map, take, tap
} from 'rxjs/operators';

import { extractDocVersion } from 'app/utils/document-version';
import { AppQuote as Quote } from 'graphql/models/extended';
import { DocumentVersionInput, FavoriteSourceInput } from 'graphql/models/generated';
import { CreateFavoriteMutation } from 'graphql/mutations/create-favorite';
import { RemoveFavoriteMutation } from 'graphql/mutations/remove-favorite';
import { UpdateOfferTitleMutation } from 'graphql/mutations/update-offer-title';
import { FavoriteQuery } from 'graphql/queries/favorite';
import { FavoritesQuery } from 'graphql/queries/favorites';
import type { Favorite, StoredFavoritePositions } from 'models/favorites.model';
import type { OfferBase } from 'models/offer.model';
import { ConfirmModalService } from 'shared/services/confirm-modal.service';
import { FavoriteStoreService } from 'shared/services/favorite-store.service';

export interface RemoveFavoriteOptions {
  confirmDialog?: boolean;
  documentVersion?: DocumentVersionInput;
  lineId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FavoriteService {
  constructor(
    private createFavoriteMutation: CreateFavoriteMutation,
    private removeFavoriteMutation: RemoveFavoriteMutation,
    private updateOfferTitleMutation: UpdateOfferTitleMutation,
    private favoritesQuery: FavoritesQuery,
    private favoriteQuery: FavoriteQuery,
    private confirmModalService: ConfirmModalService,
    private favoriteStoreService: FavoriteStoreService,
    private translateService: TranslateService,
  ) {
    //
  }

  getFavorites(modelOrProductId: string): Observable<StoredFavoritePositions> {
    return this.favoritesQuery.fetch({ modelOrProductId }, { fetchPolicy: 'no-cache' }).pipe(
      take(1),
      map((r) => r.data?.favorites as Favorite[] | undefined),
      tap((favorites) => {
        if (favorites && favorites.length) {
          this.favoriteStoreService.setFavorites(favorites);
        }
      }),
      concatMap(() => this.favoriteStoreService.getFavorites())
    );
  }

  getFavorite(documentId: string): Observable<Favorite | undefined> {
    const language = this.translateService.currentLang || this.translateService.defaultLang;
    const variables = {
      documentVersion: {
        documentId,
        revision: 0
      },
      language
    };

    return this.favoriteQuery.fetch(variables, { fetchPolicy: 'no-cache' }).pipe(
      map((r) => r.data?.favorite as Favorite | undefined),
      tap((favorite) => {
        if (favorite) {
          this.favoriteStoreService.addFavorite(favorite);
        }
      })
    );
  }

  addFavorite(
    {
      lineId, offer, productId, title
    }: {
      lineId: string; offer: OfferBase; productId: string; title: string;
    }
  ): Observable<StoredFavoritePositions> {
    const source: FavoriteSourceInput = {
      documentVersion: extractDocVersion(offer),
      lineId
    };

    title.trim();

    const variables = {
      source,
      productId,
      title
    };

    const checkTitleExist$ = this.checkUniqueTitle(productId, title);

    return checkTitleExist$.pipe(
      concatMap((val) => {
        if (val) {
          this.throwTitleUniqueError();
        }

        return this.createFavoriteMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
          map((r) => r.data?.createFavorite?.documentId as Extract<Quote, 'documentId'>),
          take(1),
          concatMap((documentId) => this.favoriteStoreService.addFavorite({ documentId, lineId, title }))
        );
      })
    );
  }

  checkUniqueTitle(modelOrProductId: string, title: string) {
    return this.getFavorites(modelOrProductId)
      .pipe(
        take(1),
        map((favorites) => this.checkExistingTitle(title, favorites))
      );
  }

  checkExistingTitle(name: string, favorites: StoredFavoritePositions): boolean {
    let lineIds = Object.keys(favorites);
    if (lineIds.length > 0) {
      const title = name.toLowerCase().trim();
      lineIds = lineIds.filter((lineId) => favorites[lineId].title.toLowerCase().trim() === title);
    }

    return lineIds.length > 0;
  }

  private removeFavoriteInternal(
    { documentId, lineId }: Pick<Partial<Favorite>, 'documentId' | 'lineId'>
  ): Observable<StoredFavoritePositions> {
    if (!lineId && !documentId) {
      throw new Error('Missing both "documentId" and "lineId"');
    }

    const variables$ = !documentId
      ? this.favoriteStoreService.getFavorite(lineId as string)
        .pipe(map((favorite) => {
          if (!favorite) {
            return undefined;
          }

          return {
            documentsVersion: [{ documentId: favorite.documentId, revision: 0 }]
          };
        }))
      : of({
        documentsVersion: [{ documentId, revision: 0 }]
      });

    return variables$.pipe(
      concatMap((variables) => (
        variables
          ? this.removeFavoriteMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
            map((r) => r.data?.removeFavorite),
            take(1)
          )
          : of(undefined))),
      concatMap(() => this.favoriteStoreService.removeFavorite({ documentId, lineId }))
    );
  }

  removeFavorite(
    favorite: Pick<Partial<Favorite>, 'documentId' | 'lineId'>,
    confirmDialog?: boolean
  ): Observable<StoredFavoritePositions> {
    if (!favorite.lineId && !favorite.documentId) {
      throw new Error('Missing both "documentId" and "lineId"');
    }

    return confirmDialog
      ? this.confirmModalService
        .show('DELETE_FAVORITE')
        .onConfirm()
        .pipe(
          concatMap(() => this.removeFavoriteInternal(favorite)),
        )
      : this.removeFavoriteInternal(favorite);
  }

  updateTitle(favorite: Favorite, name: string): Observable<StoredFavoritePositions> {
    const title = name.trim();
    const variables = {
      documentVersion: {
        documentId: favorite.documentId,
        revision: 0
      },
      title
    };
    const checkTitleExist$ = this.checkUniqueTitle(favorite.documentId as string, title);

    return checkTitleExist$.pipe(
      concatMap((val) => {
        if (val) {
          this.throwTitleUniqueError();
        }

        return this.updateOfferTitleMutation.mutate(variables, { fetchPolicy: 'no-cache' }).pipe(
          concatMap(() => this.favoriteStoreService.updateTitle(favorite, title))
        );
      })
    );
  }

  throwTitleUniqueError() {
    // eslint-disable-next-line no-throw-literal
    throw {
      form: {
        errors: {
          name: { uniqueRequired: true }
        }
      }
    };
  }
}
